import Image from 'next/image';
import ActivityTitle from '../common/ActivityTitle';
import { GuideDetails } from '@/types/activityDetailsTypes';
import React from 'react';
import FullscreenVideo from './FullscreenVideo';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { VideoIcon } from '@/design-system/icons';

interface GuideCardProps {
  guidedetails: GuideDetails;
}
const GuideCard: React.FC<GuideCardProps> = ({ guidedetails }) => {
  const [imageError, setImageError] = React.useState(false);
  const hasImage = Boolean(guidedetails?.imageUrl) && !imageError;
  const initial = guidedetails?.name
    ? guidedetails.name.trim().charAt(0).toUpperCase()
    : 'A';
  const [activeVideo, setActiveVideo] = React.useState<string | null>(null);
  const [isDescriptionPopoverOpen, setIsDescriptionPopoverOpen] =
    React.useState(false);
  const closeTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const aboutDescription = guidedetails.profileDescription
    ? guidedetails.profileDescription
    : `Hi, I\u2019m ${guidedetails.name || 'your host'}`;
  const shouldClampDescription = aboutDescription.length > 220;

  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsDescriptionPopoverOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setIsDescriptionPopoverOpen(false);
    }, 150); // 150ms delay to allow moving to content
  };

  return (
    <>
      <ActivityTitle title='Your host' className='' />
      <div className='w-full bg-white rounded-[20px] md:shadow-none shadow-bookcard px-5 md:px-6 py-5 md:py-6 border border-[#EBEBEB] mb-[1.125rem] md:h-[240px]'>
        <div className='grid grid-cols-12 gap-6 h-full'>
          <div className='xl:col-span-3 sm:col-span-4 col-span-12'>
            <div className='flex flex-col items-center'>
              <div className='w-[95px] h-[95px] md:w-[95px] md:h-[95px] relative'>
                {hasImage ? (
                  <Image
                    src={guidedetails.imageUrl}
                    alt={guidedetails.name}
                    className='rounded-full object-cover'
                    fill
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <div className='w-full h-full rounded-full border border-secondary flex items-center justify-center bg-white'>
                    <span className='md:text-2xl text-base font-semibold text-black'>
                      {initial}
                    </span>
                  </div>
                )}
              </div>
              <h2 className='md:text-[1rem] text-sm font-semibold mt-3'>
                {guidedetails.name}
              </h2>
              <p className='md:text-sm text-xs text-darkgrey mt-1 mb-2 text-center capitalize'>
                {Array.isArray(guidedetails.type)
                  ? guidedetails.type.length === 1
                    ? guidedetails.type[0]
                    : guidedetails.type.join(' / ')
                  : guidedetails.type || ''}
              </p>
              <div className='flex items-center gap-x-1 md:text-sm text-xs font-normal sm:mb-0'>
                <svg
                  width='16'
                  height='16'
                  viewBox='0 0 16 16'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                  className='md:w-4 w-3 md:h-4 h-3'
                >
                  <g clipPath='url(#clip0_240_126)'>
                    <path
                      d='M7.99967 1.33398C4.31967 1.33398 1.33301 4.32065 1.33301 8.00065C1.33301 11.6807 4.31967 14.6673 7.99967 14.6673C11.6797 14.6673 14.6663 11.6807 14.6663 8.00065C14.6663 4.32065 11.6797 1.33398 7.99967 1.33398ZM7.99967 13.334C5.05967 13.334 2.66634 10.9406 2.66634 8.00065C2.66634 5.06065 5.05967 2.66732 7.99967 2.66732C10.9397 2.66732 13.333 5.06065 13.333 8.00065C13.333 10.9406 10.9397 13.334 7.99967 13.334ZM11.0597 5.05398L6.66634 9.44732L4.93967 7.72732L3.99967 8.66732L6.66634 11.334L11.9997 6.00065L11.0597 5.05398Z'
                      fill='#34A853'
                    />
                  </g>
                  <defs>
                    <clipPath id='clip0_240_126'>
                      <rect width='16' height='16' fill='white' />
                    </clipPath>
                  </defs>
                </svg>

                {guidedetails.nexgoVerified ? (
                  <span>Nexgo Verified</span>
                ) : (
                  <span>Nexgo Not Verified</span>
                )}
              </div>
            </div>
          </div>
          <div className='xl:col-span-9 sm:col-span-8 col-span-12 md:pt-1'>
            <h3 className='md:text-sm text-xs font-semibold'>About me</h3>
            <div className='mt-2'>
              {shouldClampDescription ? (
                <Popover
                  open={isDescriptionPopoverOpen}
                  onOpenChange={setIsDescriptionPopoverOpen}
                >
                  <PopoverTrigger asChild>
                    <p
                      className='md:text-sm text-xs leading-6 text-darkgrey cursor-help'
                      style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                    >
                      {aboutDescription}
                    </p>
                  </PopoverTrigger>
                  <PopoverContent
                    className='max-w-3xl w-[40rem] max-w-[min(90vw,40rem)] text-sm leading-6 text-darkgrey'
                    side='top'
                    align='start'
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onOpenAutoFocus={e => e.preventDefault()} // Prevent stealing focus
                  >
                    <p className='max-h-[300px] overflow-y-auto pr-1'>
                      {aboutDescription}
                    </p>
                  </PopoverContent>
                </Popover>
              ) : (
                <p className='md:text-sm text-xs leading-6 text-darkgrey'>
                  {aboutDescription}
                </p>
              )}
            </div>
            <p className='md:text-sm text-xs text-darkgrey mt-3 capitalize'>
              <span className='font-medium capitalize'>Speaks:</span>{' '}
              {guidedetails.languages && guidedetails.languages.join(', ')}
            </p>
            {guidedetails?.guideIntroVideoUrl && (
              <div className='mt-3'>
                <button
                  type='button'
                  onClick={() =>
                    setActiveVideo(guidedetails.guideIntroVideoUrl!)
                  }
                  className='flex items-center justify-center w-[200px] h-10 px-[13px] py-2 gap-[10px] border border-[#D9D9D9] rounded-[20px] text-[12px] leading-[15px] font-semibold text-black text-center hover:bg-gray-50'
                >
                  <VideoIcon size={20} color='currentColor' />
                  Hear it from{' '}
                  {guidedetails.name?.split(' ')?.[0] || 'the host'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {activeVideo && (
        <FullscreenVideo
          src={activeVideo}
          onClose={() => setActiveVideo(null)}
        />
      )}
    </>
  );
};
export default GuideCard;
